import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { BaseSSEProgressService } from '../sse/base-sse-progress.service';

export enum AreaMarketGenerationStatus {
  PENDING = 'PENDING',
  LOADING_DATA = 'LOADING_DATA',
  CREATING_AREAS = 'CREATING_AREAS',
  CREATING_ELEMENTS = 'CREATING_ELEMENTS',
  TRANSLATING_FILES = 'TRANSLATING_FILES',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR'
}

export interface AreaMarketGenerationProgress {
  // Core identification
  id: string;
  jobId: string;
  projectId: string;
  sessionId?: string;
  marketName?: string;

  // Current status and step
  status: AreaMarketGenerationStatus;
  currentStep: AreaMarketGenerationStatus;
  message: string;

  // Progress tracking
  processedAreas: number;
  totalAreas: number;
  processedElements: number;
  totalElements: number;

  // Current work context
  currentAreaId?: string;
  currentAreaTitle?: string;
  currentElementName?: string;
  fileTranslationTitle?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AreaMarketGenerationProgressService extends BaseSSEProgressService<AreaMarketGenerationProgress> {
  // Alias for better semantic naming
  public get generationProgress(): BehaviorSubject<AreaMarketGenerationProgress[] | null> {
    return this.progress;
  }

  /**
   * Watch area market generation progress for a specific project.
   *
   * @param projectId
   *
   * @returns Observable of area market generation progress updates.
   */
  public watchProgress(projectId: string): Observable<AreaMarketGenerationProgress> {
    return this.watchSSEProgress(projectId);
  }

  /**
   * Stop watching area market generation progress for a specific project.
   *
   * @param projectId
   */
  public stopWatching(projectId: string): void {
    this.stopWatchingSSE(projectId);
  }

  /**
   * Build the SSE topic URL for area market generation.
   *
   * @param projectId
   *
   * @returns
   */
  protected buildTopic(projectId: string): string {
    return `/projects/${projectId}/areas/market-generation-events`;
  }

  /**
   * Build the connection ID for area market generation.
   *
   * @param projectId
   *
   * @returns
   */
  protected buildConnectionId(projectId: string): string {
    return `area-market-${projectId}`;
  }

  /**
   * Get the progress ID from a progress object.
   *
   * @param progress The progress object.
   *
   * @returns
   */
  protected getProgressId(progress: AreaMarketGenerationProgress): string {
    return progress.id;
  }

  /**
   * Hydrate progress data from SSE message.
   *
   * @param data
   * @param projectId
   *
   * @returns Hydrated progress object.
   */
  protected hydrateProgressEvent(data: Partial<AreaMarketGenerationProgress>, projectId: string): AreaMarketGenerationProgress {
    const status = data.currentStep && Object.values(AreaMarketGenerationStatus).includes(data.currentStep)
      ? data.currentStep as AreaMarketGenerationStatus
      : AreaMarketGenerationStatus.LOADING_DATA;

    return {
      id: data.id ?? `${projectId}#${data.marketName}`,
      jobId: data.jobId ?? `${projectId}#${data.marketName}`,
      projectId: data.projectId || projectId,
      sessionId: data.sessionId,
      marketName: data.marketName,
      status,
      currentStep: data.currentStep || AreaMarketGenerationStatus.LOADING_DATA,
      message: data.message || 'Processing...',
      processedAreas: data.processedAreas || 0,
      totalAreas: data.totalAreas || 0,
      processedElements: data.processedElements || 0,
      totalElements: data.totalElements || 0,
      currentAreaId: data.currentAreaId,
      currentAreaTitle: data.currentAreaTitle,
      currentElementName: data.currentElementName,
      fileTranslationTitle: data.fileTranslationTitle
    };
  }
}
